import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import DocumentPreviewModal from './DocumentPreviewModal';
import SignaturePad from './SignaturePad';
import BadgeScanStep from './BadgeScanStep';
import { useLanguage } from '../i18n/LanguageContext';

const REFERENCE_MIN_CHARS = 3;

export default function RegisterModal({ onClose }) {
  const { t, language } = useLanguage();
  const [form, setForm] = useState({
    nome: '',
    cognome: '',
    numero_badge: '',
    persona_riferimento: '',
    ditta: '',
    tipo_documento: '',
    numero_documento: '',
    email: '',
    note: '',
    privacy_accepted: false,
    security_accepted: false,
  });
  const [signatureData, setSignatureData] = useState(null);
  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [signatureRequired, setSignatureRequired] = useState(true);
  const [documentFieldEnabled, setDocumentFieldEnabled] = useState(true);
  // null = configurazione non ancora caricata; true = il badge si legge alla
  // fine dal lettore NFC; false = modalità storica (campo badge nel modulo)
  const [nfcFirst, setNfcFirst] = useState(null);
  const [step, setStep] = useState('form'); // 'form' | 'badge'
  const [badgeError, setBadgeError] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const [referenceSuggestions, setReferenceSuggestions] = useState([]);
  const [showReferenceSuggestions, setShowReferenceSuggestions] = useState(false);
  const [referenceContactId, setReferenceContactId] = useState(null);
  const referenceDebounceRef = useRef(null);
  const referenceWrapRef = useRef(null);

  useEffect(() => {
    api.getSignatureConfig()
      .then((res) => {
        setSignatureEnabled(Boolean(res?.data?.enabled));
        setSignatureRequired(res?.data?.required !== false);
      })
      .catch(() => setSignatureEnabled(false));

    api.getDocumentFieldConfig()
      .then((res) => setDocumentFieldEnabled(res?.data?.enabled !== false))
      .catch(() => setDocumentFieldEnabled(true));

    api.getBadgeRegistrationConfig()
      .then((res) => setNfcFirst(res?.data?.nfcFirst === true))
      .catch(() => setNfcFirst(false));
  }, []);

  // Chiude il menu dei suggerimenti se si tocca fuori dal campo
  useEffect(() => {
    if (!showReferenceSuggestions) return undefined;
    const handleClickOutside = (e) => {
      if (referenceWrapRef.current && !referenceWrapRef.current.contains(e.target)) {
        setShowReferenceSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showReferenceSuggestions]);

  const update = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleReferenceChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, persona_riferimento: value }));
    setReferenceContactId(null); // qualsiasi modifica manuale invalida la selezione precedente

    if (referenceDebounceRef.current) clearTimeout(referenceDebounceRef.current);

    if (value.trim().length < REFERENCE_MIN_CHARS) {
      setReferenceSuggestions([]);
      setShowReferenceSuggestions(false);
      return;
    }

    referenceDebounceRef.current = setTimeout(async () => {
      try {
        const res = await api.searchReferenceContacts(value.trim());
        setReferenceSuggestions(res.data || []);
        setShowReferenceSuggestions(true);
      } catch {
        setReferenceSuggestions([]);
      }
    }, 300);
  };

  const handleSelectReference = (contact) => {
    setForm((f) => ({ ...f, persona_riferimento: `${contact.nome} ${contact.cognome}` }));
    setReferenceContactId(contact.id);
    setShowReferenceSuggestions(false);
    setReferenceSuggestions([]);
  };

  const sendRegistration = async (numeroBadge) => {
    setSubmitting(true);
    try {
      await api.register({
        ...form,
        numero_badge: numeroBadge,
        signature_data: signatureEnabled ? signatureData : undefined,
        language,
        persona_riferimento_id: referenceContactId || undefined,
      });
      setSuccess(true);
      return true;
    } catch (err) {
      return err.message;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const badgeRequiredHere = nfcFirst === false;
    if (!form.nome.trim() || !form.cognome.trim() || (badgeRequiredHere && !form.numero_badge.trim())) {
      setError(badgeRequiredHere ? t('errorRequiredFields') : t('errorRequiredFieldsNoBadge'));
      return;
    }
    if (!form.privacy_accepted || !form.security_accepted) {
      setError(t('errorConsent'));
      return;
    }
    if (signatureEnabled && signatureRequired && !signatureData) {
      setError(t('errorSignatureRequired'));
      return;
    }

    // Con la lettura NFC finale, i dati sono a posto: si passa al badge
    if (nfcFirst) {
      setBadgeError('');
      setStep('badge');
      return;
    }

    const result = await sendRegistration(form.numero_badge);
    if (result !== true) setError(result);
  };

  const handleBadge = async (badge) => {
    setBadgeError('');
    const result = await sendRegistration(badge);
    if (result !== true) setBadgeError(result);
  };

  return (
    <div className="modal-overlay" onClick={step === 'badge' && !success ? undefined : onClose}>
      <div className="modal-card modal-card--wide" onClick={(e) => e.stopPropagation()}>
        {success ? (
          <div className="form-success">
            <div className="success-icon">✓</div>
            <h2>{t('registerSuccessTitle')}</h2>
            <p className="modal-subtitle">{t('registerSuccessSubtitle')}</p>
            <button className="btn btn-primary" onClick={onClose}>{t('close')}</button>
          </div>
        ) : (
          <>
            {step === 'badge' && (
              <BadgeScanStep
                onBadge={handleBadge}
                onBack={() => { setStep('form'); setBadgeError(''); }}
                error={badgeError}
                busy={submitting}
              />
            )}
            {/* Il modulo resta montato (solo nascosto) durante il passo badge:
                così firma e campi compilati si ritrovano intatti se si torna indietro */}
            <div style={{ display: step === 'badge' ? 'none' : 'block' }}>
            <h2>{t('registerTitle')}</h2>
            <p className="modal-subtitle">{t('registerSubtitle')}</p>

            {error && <div className="form-error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="badge-card">
                <div className="field-row">
                  <div className="field">
                    <label htmlFor="nome">{t('fieldNome')}</label>
                    <input id="nome" type="text" value={form.nome} onChange={update('nome')} autoComplete="given-name" />
                  </div>
                  <div className="field">
                    <label htmlFor="cognome">{t('fieldCognome')}</label>
                    <input id="cognome" type="text" value={form.cognome} onChange={update('cognome')} autoComplete="family-name" />
                  </div>
                </div>
                <div className="field-row">
                  {nfcFirst === false && (
                    <div className="field">
                      <label htmlFor="badge">{t('fieldBadge')}</label>
                      <input id="badge" type="text" value={form.numero_badge} onChange={update('numero_badge')} />
                    </div>
                  )}
                  {/* Senza il campo badge, "Azienda" occupa entrambe le colonne */}
                  <div className="field" style={nfcFirst !== false ? { gridColumn: '1 / -1' } : undefined}>
                    <label htmlFor="ditta">{t('fieldDitta')} <span className="optional-tag">{t('optional')}</span></label>
                    <input id="ditta" type="text" value={form.ditta} onChange={update('ditta')} />
                  </div>
                </div>
                {documentFieldEnabled && (
                  <div className="field-row">
                    <div className="field">
                      <label htmlFor="tipo-documento">{t('fieldDocumentType')} <span className="optional-tag">{t('optional')}</span></label>
                      <select id="tipo-documento" value={form.tipo_documento} onChange={update('tipo_documento')}>
                        <option value="">{t('docTypePlaceholder')}</option>
                        <option value="carta_identita">{t('docTypeIdCard')}</option>
                        <option value="patente">{t('docTypeLicense')}</option>
                        <option value="passaporto">{t('docTypePassport')}</option>
                      </select>
                    </div>
                    <div className="field">
                      <label htmlFor="numero-documento">{t('fieldDocumentNumber')} <span className="optional-tag">{t('optional')}</span></label>
                      <input id="numero-documento" type="text" value={form.numero_documento} onChange={update('numero_documento')} />
                    </div>
                  </div>
                )}
                <div className="field autocomplete-wrap" ref={referenceWrapRef}>
                  <label htmlFor="riferimento">{t('fieldRiferimento')} <span className="optional-tag">{t('optional')}</span></label>
                  <input
                    id="riferimento"
                    type="text"
                    value={form.persona_riferimento}
                    onChange={handleReferenceChange}
                    onFocus={() => { if (referenceSuggestions.length) setShowReferenceSuggestions(true); }}
                    autoComplete="off"
                  />
                  {showReferenceSuggestions && referenceSuggestions.length > 0 && (
                    <ul className="autocomplete-list">
                      {referenceSuggestions.map((c) => (
                        <li key={c.id}>
                          <button type="button" onClick={() => handleSelectReference(c)}>
                            {c.nome} {c.cognome}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {referenceContactId && (
                    <span className="field-hint">{t('referenceSelectedHint')}</span>
                  )}
                </div>
                <div className="field">
                  <label htmlFor="email">{t('fieldEmail')} <span className="optional-tag">{t('optional')}</span></label>
                  <input id="email" type="email" value={form.email} onChange={update('email')} autoComplete="email" placeholder={t('emailPlaceholder')} />
                  <span className="field-hint">{t('emailHint')}</span>
                </div>
                <div className="field">
                  <label htmlFor="note">{t('fieldNote')} <span className="optional-tag">{t('optional')}</span></label>
                  <textarea id="note" rows={2} maxLength={1000} value={form.note} onChange={update('note')} />
                </div>
              </div>

              <div className="register-consents">
                <button
                  type="button"
                  className={`consent-card ${form.privacy_accepted ? 'consent-card--accepted' : ''}`}
                  onClick={() => setPreviewDoc('privacy')}
                >
                  <span className="consent-card__icon" aria-hidden="true">
                    {form.privacy_accepted ? (
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M7 3h7l4 4v14H7V3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M14 3v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>
                    )}
                  </span>
                  <span className="consent-card__text">
                    <span className="consent-card__label">{t('linkPrivacy')}</span>
                    <span className="consent-card__status">
                      {form.privacy_accepted ? t('consentDone') : t('consentToRead')}
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  className={`consent-card ${form.security_accepted ? 'consent-card--accepted' : ''}`}
                  onClick={() => setPreviewDoc('security')}
                >
                  <span className="consent-card__icon" aria-hidden="true">
                    {form.security_accepted ? (
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M7 3h7l4 4v14H7V3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M14 3v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>
                    )}
                  </span>
                  <span className="consent-card__text">
                    <span className="consent-card__label">{t('linkSecurity')}</span>
                    <span className="consent-card__status">
                      {form.security_accepted ? t('consentDone') : t('consentToRead')}
                    </span>
                  </span>
                </button>
              </div>

              {signatureEnabled && (
                <SignaturePad
                  label={signatureRequired ? t('fieldSignature') : `${t('fieldSignature')} ${t('optional')}`}
                  clearLabel={t('signatureClear')}
                  hintText={t('signatureHint')}
                  onChange={setSignatureData}
                />
              )}

              <div className="btn-row" style={{ marginTop: 20 }}>
                <button type="button" className="btn btn-ghost" onClick={onClose}>{t('cancel')}</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? t('submitting') : nfcFirst ? t('registerNext') : t('registerTitle')}
                </button>
              </div>
            </form>
            </div>
          </>
        )}
      </div>

      {previewDoc && (
        <DocumentPreviewModal
          docType={previewDoc}
          onClose={() => setPreviewDoc(null)}
          requireAcceptance
          onAccept={() => setForm((f) => ({ ...f, [`${previewDoc === 'privacy' ? 'privacy' : 'security'}_accepted`]: true }))}
        />
      )}
    </div>
  );
}
